import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import NewsletterCampaignDialog from '../../components/NewsletterCampaignDialog';
import NewsletterKpiTab from '../../components/NewsletterKpiTab';
import {
  Mail, Loader2, Send, Eye, Trash2, RefreshCw, Plus, MousePointerClick, AlertTriangle,
  Sparkles, Sun, Zap, Gift, Image as ImageIcon, ChevronDown, ChevronUp, CalendarClock, XCircle, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_CTA = 'https://www.holidayworld.it';

const AUDIENCES = [
  { key: 'guest', label: 'Clienti / Ospiti', hint: 'solo con consenso marketing' },
  { key: 'partner', label: 'Partner', hint: 'rubrica partner' },
  { key: 'owner', label: 'Proprietari', hint: 'case attive' },
  { key: 'manager', label: 'Responsabili', hint: 'property manager' },
];

const NL_TYPE_META = {
  novita: { label: 'Novità', Icon: Sparkles, cls: 'bg-blue-100 text-blue-800' },
  inizio_stagione: { label: 'Inizio Stagione', Icon: Sun, cls: 'bg-amber-100 text-amber-800' },
  lastminute: { label: 'Last Minute', Icon: Zap, cls: 'bg-red-100 text-red-700' },
  offerta_speciale: { label: 'Offerta Speciale', Icon: Gift, cls: 'bg-emerald-100 text-emerald-800' },
};

const LANGS = ['it', 'en', 'de', 'fr', 'es', 'pt', 'pl', 'nl'];

const STATUS_BADGE = {
  draft: { label: 'Bozza da approvare', cls: 'bg-amber-100 text-amber-800' },
  scheduled: { label: 'Programmata', cls: 'bg-violet-100 text-violet-800' },
  dispatching: { label: 'In partenza…', cls: 'bg-blue-100 text-blue-800' },
  sending: { label: 'In invio…', cls: 'bg-blue-100 text-blue-800' },
  sent: { label: 'Inviata', cls: 'bg-emerald-100 text-emerald-800' },
};

const fmtSlot = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('it-IT', {
    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome',
  });
};

export default function NewsletterPage() {
  const { user } = useAuth();
  const token = localStorage.getItem('hw_token');
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewAudience, setPreviewAudience] = useState('guest');
  const [previewLang, setPreviewLang] = useState('it');
  const [previewSubject, setPreviewSubject] = useState('');
  const [quality, setQuality] = useState(null);
  const [counts, setCounts] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [nextSlot, setNextSlot] = useState(null);
  const [trDesc, setTrDesc] = useState('');
  const [trFailed, setTrFailed] = useState(false);
  const [audiencesOn, setAudiencesOn] = useState({ guest: true, partner: true, owner: true, manager: true });
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [busy, setBusy] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [propSearch, setPropSearch] = useState('');
  const [propResults, setPropResults] = useState([]);
  const [campaignProp, setCampaignProp] = useState(null);
  const [view, setView] = useState('campagne');
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/newsletters`, H);
      setItems(res.data.newsletters || []);
    } catch (e) {
      toast.error('Errore caricamento newsletter');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId && items.length) {
      const nl = items.find((i) => i.id === openId);
      if (nl) {
        setSearchParams({}, { replace: true });
        openDetail(nl);
      }
    }
  }, [items]); // eslint-disable-line

  const refreshPreview = async (nlId, aud, lang) => {
    try {
      const pv = await axios.get(`${API}/newsletters/${nlId}/preview?audience=${aud}&lang=${lang}`, H);
      setPreviewHtml(pv.data.html);
      setQuality(pv.data.quality);
      setPreviewSubject(pv.data.subject || '');
      setTrDesc(pv.data.description_translated || '');
      setTrFailed(!!pv.data.translation_failed);
    } catch (_) { /* noop */ }
  };

  const openDetail = async (nl) => {
    setSelected({ ...nl });
    setPreviewHtml('');
    setCounts(null);
    setPhotos([]);
    setPhotosOpen(false);
    try {
      const [pv, rc, ph] = await Promise.all([
        axios.get(`${API}/newsletters/${nl.id}/preview?audience=${previewAudience}&lang=${previewLang}`, H),
        axios.get(`${API}/newsletters/${nl.id}/recipients`, H),
        axios.get(`${API}/newsletters/${nl.id}/photos`, H),
      ]);
      setPreviewHtml(pv.data.html);
      setQuality(pv.data.quality);
      setPreviewSubject(pv.data.subject || '');
      setTrDesc(pv.data.description_translated || '');
      setTrFailed(!!pv.data.translation_failed);
      setCounts(rc.data);
      setPhotos(ph.data.photos || []);
      if (nl.status === 'draft' || nl.status === 'scheduled') {
        try {
          const slot = await axios.get(`${API}/newsletters/schedule/next-slot`, H);
          setNextSlot(slot.data);
        } catch (_) { setNextSlot(null); }
      }
    } catch (e) {
      toast.error('Errore anteprima');
    }
  };

  const switchAudience = async (aud) => {
    setPreviewAudience(aud);
    if (!selected) return;
    await refreshPreview(selected.id, aud, aud === 'guest' ? previewLang : 'it');
  };

  const switchLang = async (lang) => {
    setPreviewLang(lang);
    if (!selected) return;
    await refreshPreview(selected.id, 'guest', lang);
  };

  const saveEdits = async () => {
    setBusy('save');
    try {
      const res = await axios.patch(`${API}/newsletters/${selected.id}`, {
        subject: selected.subject,
        cta_url: selected.cta_url,
        custom_message: selected.custom_message,
        offer_start: selected.offer_start || '',
        offer_end: selected.offer_end || '',
        discount_percent: selected.discount_percent !== '' && selected.discount_percent != null
          ? parseFloat(selected.discount_percent) : null,
        offer_price: selected.offer_price !== '' && selected.offer_price != null
          ? parseFloat(selected.offer_price) : null,
      }, H);
      setSelected({ ...res.data.newsletter });
      await refreshPreview(selected.id, previewAudience, previewAudience === 'guest' ? previewLang : 'it');
      toast.success('Bozza aggiornata');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setBusy('');
    }
  };

  const pickPhoto = async (url) => {
    try {
      const res = await axios.patch(`${API}/newsletters/${selected.id}`, { hero_url: url }, H);
      setSelected({ ...res.data.newsletter });
      await refreshPreview(selected.id, previewAudience, previewAudience === 'guest' ? previewLang : 'it');
      toast.success('Foto di copertina aggiornata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore cambio foto');
    }
  };

  const saveTranslation = async () => {
    try {
      await axios.patch(`${API}/newsletters/${selected.id}`,
        { description_i18n_patch: { lang: previewLang, text: trDesc } }, H);
      await refreshPreview(selected.id, 'guest', previewLang);
      toast.success(`Traduzione ${previewLang.toUpperCase()} salvata`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio traduzione');
    }
  };

  const sendTest = async () => {
    setBusy('test');
    try {
      const res = await axios.post(`${API}/newsletters/${selected.id}/test`,
        { email: testEmail, audience: previewAudience, lang: previewLang }, H);
      toast.success(res.data.simulated
        ? 'Test simulato (ambiente preview: nessuna email reale)'
        : `Email di test inviata a ${testEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio test');
    } finally {
      setBusy('');
    }
  };

  const approveAndSend = async (mode) => {
    const on = AUDIENCES.filter((a) => audiencesOn[a.key]);
    if (!on.length) { toast.error('Seleziona almeno un gruppo'); return; }
    const tot = counts ? on.reduce((s, a) => s + (counts.counts[a.key] || 0), 0) : '?';
    const msg = mode === 'schedule'
      ? `⏰ PROGRAMMA INVIO\n\nNewsletter "${selected.property_name}" a ~${tot} destinatari:\n${on.map((a) => `  • ${a.label}`).join('\n')}\n\nPartirà automaticamente ${nextSlot?.label || 'il prossimo giovedì alle 14:00'}.\nSe altri giovedì sono già occupati, slitta al successivo.\n\nConfermi la programmazione?`
      : `🚀 INVIA SUBITO\n\nNewsletter "${selected.property_name}" a ~${tot} destinatari:\n${on.map((a) => `  • ${a.label}`).join('\n')}\n\nGli ospiti stranieri la riceveranno nella loro lingua.\nConfermi l'invio immediato?`;
    if (!window.confirm(msg)) return;
    setBusy('send');
    try {
      const res = await axios.post(`${API}/newsletters/${selected.id}/send`,
        { audiences: on.map((a) => a.key), mode }, H);
      if (res.data.scheduled) {
        toast.success(`Newsletter programmata per ${res.data.scheduled_label}`);
      } else {
        toast.success(`Invio avviato: ${res.data.queued} email in coda${res.data.real_send ? '' : ' (SIMULATO in preview)'}`);
      }
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio');
    } finally {
      setBusy('');
    }
  };

  const cancelSchedule = async () => {
    if (!window.confirm('Annullare la programmazione? La newsletter tornerà in bozza.')) return;
    try {
      await axios.post(`${API}/newsletters/${selected.id}/cancel-schedule`, {}, H);
      toast.success('Programmazione annullata: tornata in bozza');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const sendScheduledNow = async () => {
    if (!window.confirm(`🚀 Inviare SUBITO "${selected.property_name}" senza aspettare lo slot programmato?`)) return;
    setBusy('send');
    try {
      const res = await axios.post(`${API}/newsletters/${selected.id}/send`,
        { audiences: selected.scheduled_audiences || AUDIENCES.map((a) => a.key), mode: 'now' }, H);
      toast.success(`Invio avviato: ${res.data.queued} email in coda${res.data.real_send ? '' : ' (SIMULATO in preview)'}`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio');
    } finally {
      setBusy('');
    }
  };

  const removeNewsletter = async (nl) => {
    if (!window.confirm(`Eliminare la newsletter di "${nl.property_name}"?`)) return;
    try {
      await axios.delete(`${API}/newsletters/${nl.id}`, H);
      toast.success('Eliminata');
      if (selected?.id === nl.id) setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const searchProps = async (q) => {
    setPropSearch(q);
    if (q.length < 2) { setPropResults([]); return; }
    try {
      const res = await axios.get(`${API}/properties?fields=id,name,city,property_code`, H);
      const list = Array.isArray(res.data) ? res.data : res.data.properties || [];
      setPropResults(list.filter((p) => (p.name || '').toLowerCase().includes(q.toLowerCase())).slice(0, 8));
    } catch (_) { setPropResults([]); }
  };

  const syncGuests = async () => {
    setBusy('syncguests');
    try {
      const res = await axios.post(`${API}/newsletters/guest-contacts/sync`, {}, H);
      toast.success(`Contatti ospiti: +${res.data.imported} importati dalle prenotazioni · ${res.data.total_contacts} totali attivi`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore sincronizzazione'); }
    finally { setBusy(''); }
  };

  const selType = selected ? (NL_TYPE_META[selected.nl_type] || NL_TYPE_META.novita) : null;
  const showOfferFields = selected && ['lastminute', 'offerta_speciale'].includes(selected.nl_type);
  const linkMissing = selected && (!selected.cta_url || selected.cta_url === DEFAULT_CTA);

  return (
    <div className="p-6 space-y-4" data-testid="newsletter-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="h-7 w-7 text-blue-800" /> Newsletter
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Campagne per casa: Novità, Inizio Stagione, Last Minute, Offerta Speciale — gli ospiti stranieri ricevono l'email nella loro lingua
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={syncGuests} disabled={busy === 'syncguests'}
            title="Importa nei contatti marketing tutti gli ospiti con email reale dalle prenotazioni (consenso implicito HW Smart)"
            data-testid="newsletter-sync-guests-btn">
            {busy === 'syncguests' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Users className="h-4 w-4 mr-1" />} Sincronizza ospiti
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="newsletter-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Aggiorna
          </Button>
          <Button size="sm" onClick={() => setShowNew((v) => !v)} data-testid="newsletter-new-btn">
            <Plus className="h-4 w-4 mr-1" /> Nuova campagna
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b">
        <button onClick={() => setView('campagne')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${view === 'campagne' ? 'border-blue-700 text-blue-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="newsletter-tab-campagne">
          Campagne
        </button>
        <button onClick={() => setView('kpi')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${view === 'kpi' ? 'border-blue-700 text-blue-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="newsletter-tab-kpi">
          📊 Performance
        </button>
      </div>

      {view === 'kpi' && <NewsletterKpiTab />}

      {view === 'campagne' && showNew && (
        <Card className="p-4">
          <Input
            placeholder="Cerca casa per nome…"
            value={propSearch}
            onChange={(e) => searchProps(e.target.value)}
            data-testid="newsletter-prop-search"
          />
          {propResults.length > 0 && (
            <div className="mt-2 divide-y border rounded-md">
              {propResults.map((p) => (
                <button
                  key={p.id}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between"
                  onClick={() => setCampaignProp(p)}
                  data-testid={`newsletter-prop-pick-${p.id}`}
                >
                  <span>{p.name}</span>
                  <span className="text-slate-400">{p.city} · {p.property_code}</span>
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      <NewsletterCampaignDialog
        property={campaignProp}
        open={!!campaignProp}
        onOpenChange={(v) => { if (!v) setCampaignProp(null); }}
        onCreated={async (nl) => {
          setCampaignProp(null);
          setShowNew(false); setPropSearch(''); setPropResults([]);
          await load();
          openDetail(nl);
        }}
      />

      {view === 'campagne' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-2">
          {loading ? (
            <Card className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></Card>
          ) : items.length === 0 ? (
            <Card className="p-6 text-sm text-slate-500 text-center">
              Nessuna newsletter: creane una con «Nuova campagna» o dal pulsante sulla scheda casa
            </Card>
          ) : items.map((nl) => {
            const st = STATUS_BADGE[nl.status] || STATUS_BADGE.draft;
            const tm = NL_TYPE_META[nl.nl_type] || NL_TYPE_META.novita;
            return (
              <Card
                key={nl.id}
                className={`p-3 cursor-pointer hover:border-blue-300 ${selected?.id === nl.id ? 'border-blue-500 ring-1 ring-blue-200' : ''}`}
                onClick={() => openDetail(nl)}
                data-testid={`newsletter-card-${nl.id}`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm">{nl.property_name}</p>
                  <Badge className={`${st.cls} text-[10px]`}>{st.label}</Badge>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className={`${tm.cls} border-0 text-[10px]`} data-testid={`newsletter-type-badge-${nl.id}`}>
                    <tm.Icon className="h-3 w-3 mr-1" /> {tm.label}
                  </Badge>
                  {nl.created_by === 'auto' && (
                    <Badge variant="outline" className="text-[9px] text-emerald-700 border-emerald-300 bg-emerald-50" data-testid={`newsletter-auto-badge-${nl.id}`}>
                      <Sparkles className="h-3 w-3 mr-0.5" /> Proposta automatica
                    </Badge>
                  )}
                  <p className="text-xs text-slate-500">{nl.city} · {(nl.created_at || '').slice(0, 10)}</p>
                </div>
                {nl.status === 'scheduled' && (
                  <p className="text-xs text-violet-700 mt-1 flex items-center gap-1" data-testid={`newsletter-scheduled-info-${nl.id}`}>
                    <CalendarClock className="h-3 w-3" /> {fmtSlot(nl.scheduled_at)}
                  </p>
                )}
                {nl.status === 'sent' && (
                  <p className="text-xs text-slate-600 mt-1 flex items-center gap-3">
                    <span><Send className="h-3 w-3 inline" /> {nl.stats?.sent}</span>
                    <span><Eye className="h-3 w-3 inline" /> {nl.stats?.opens}</span>
                    <span><MousePointerClick className="h-3 w-3 inline" /> {nl.stats?.clicks}</span>
                    {nl.simulated && <Badge variant="outline" className="text-[9px]">simulata</Badge>}
                  </p>
                )}
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-red-500"
                    onClick={(e) => { e.stopPropagation(); removeNewsletter(nl); }}
                    data-testid={`newsletter-delete-${nl.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        <div className="lg:col-span-2">
          {!selected ? (
            <Card className="p-10 text-center text-sm text-slate-400">Seleziona una newsletter per l'anteprima</Card>
          ) : (
            <Card className="p-4 space-y-3" data-testid="newsletter-detail">
              <div className="flex items-center gap-2">
                {selType && (
                  <Badge className={`${selType.cls} text-xs`} data-testid="newsletter-detail-type">
                    <selType.Icon className="h-3.5 w-3.5 mr-1" /> {selType.label}
                  </Badge>
                )}
                <span className="text-sm font-semibold">{selected.property_name}</span>
                <span className="text-xs text-slate-400">{selected.property_code}</span>
              </div>

              {quality && (!quality.has_photo || !quality.has_description) && (
                <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4" />
                  Guardrail: {!quality.has_photo && 'manca la foto'}{!quality.has_photo && !quality.has_description && ' e '}
                  {!quality.has_description && 'manca la descrizione'} — completa la scheda casa prima dell'invio
                </div>
              )}
              {linkMissing && (
                <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800"
                  data-testid="newsletter-link-warning">
                  <AlertTriangle className="h-4 w-4" />
                  Link holidayworld.it non impostato sulla scheda casa: il pulsante porterà alla homepage. Impostalo qui sotto nel «Link CTA» o nella scheda immobile.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Oggetto (italiano — le altre lingue sono tradotte in automatico)</label>
                  <Input value={selected.subject || ''} disabled={selected.status !== 'draft'}
                    onChange={(e) => setSelected({ ...selected, subject: e.target.value })}
                    data-testid="newsletter-subject-input" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Link CTA (casa su holidayworld.it)</label>
                  <Input value={selected.cta_url || ''} disabled={selected.status !== 'draft'}
                    onChange={(e) => setSelected({ ...selected, cta_url: e.target.value })}
                    data-testid="newsletter-cta-input" />
                </div>
              </div>

              {showOfferFields && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border rounded-md p-2 bg-slate-50" data-testid="newsletter-offer-editor">
                  <div>
                    <label className="text-xs text-slate-500">Periodo dal{selected.nl_type === 'lastminute' && ' *'}</label>
                    <Input type="date" value={selected.offer_start || ''} disabled={selected.status !== 'draft'}
                      onChange={(e) => setSelected({ ...selected, offer_start: e.target.value })}
                      data-testid="newsletter-offer-start" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">al{selected.nl_type === 'lastminute' && ' *'}</label>
                    <Input type="date" value={selected.offer_end || ''} disabled={selected.status !== 'draft'}
                      onChange={(e) => setSelected({ ...selected, offer_end: e.target.value })}
                      data-testid="newsletter-offer-end" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Sconto %{selected.nl_type === 'offerta_speciale' && ' *'}</label>
                    <Input type="number" value={selected.discount_percent ?? ''} disabled={selected.status !== 'draft'}
                      onChange={(e) => setSelected({ ...selected, discount_percent: e.target.value })}
                      data-testid="newsletter-discount-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Prezzo da €/notte</label>
                    <Input type="number" value={selected.offer_price ?? ''} disabled={selected.status !== 'draft'}
                      onChange={(e) => setSelected({ ...selected, offer_price: e.target.value })}
                      data-testid="newsletter-price-input" />
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs text-slate-500">Messaggio extra (opzionale)</label>
                <Textarea rows={2} value={selected.custom_message || ''} disabled={selected.status !== 'draft'}
                  onChange={(e) => setSelected({ ...selected, custom_message: e.target.value })}
                  data-testid="newsletter-message-input" />
              </div>

              <div className="flex items-center gap-2">
                {selected.status === 'draft' && (
                  <Button variant="outline" size="sm" onClick={saveEdits} disabled={busy === 'save'}
                    data-testid="newsletter-save-btn">
                    {busy === 'save' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva modifiche'}
                  </Button>
                )}
                {photos.length > 0 && selected.status === 'draft' && (
                  <Button variant="outline" size="sm" onClick={() => setPhotosOpen((v) => !v)}
                    data-testid="newsletter-photos-toggle">
                    <ImageIcon className="h-4 w-4 mr-1" /> Scegli foto ({photos.length})
                    {photosOpen ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                  </Button>
                )}
              </div>

              {photosOpen && (
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2 max-h-56 overflow-y-auto border rounded-md p-2"
                  data-testid="newsletter-photo-grid">
                  {photos.map((ph, i) => (
                    <button key={i} type="button" onClick={() => pickPhoto(ph.url)}
                      className={`relative rounded overflow-hidden border-2 ${selected.hero_url === ph.url ? 'border-blue-600 ring-2 ring-blue-300' : 'border-transparent hover:border-slate-300'}`}
                      data-testid={`newsletter-photo-pick-${i}`}>
                      <img src={ph.url} alt="" className="h-16 w-full object-cover" loading="lazy" />
                      {selected.hero_url === ph.url && (
                        <span className="absolute top-0.5 right-0.5 bg-blue-600 text-white text-[9px] px-1 rounded">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-1 border-b pb-2 flex-wrap">
                {AUDIENCES.map((a) => (
                  <Button key={a.key} size="sm" variant={previewAudience === a.key ? 'default' : 'ghost'}
                    className="h-7 text-xs" onClick={() => switchAudience(a.key)}
                    data-testid={`newsletter-audience-tab-${a.key}`}>
                    {a.label}{counts ? ` (${counts.counts[a.key] || 0})` : ''}
                  </Button>
                ))}
              </div>

              {previewAudience === 'guest' && (
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-[11px] text-slate-400 mr-1">Lingua anteprima:</span>
                  {LANGS.map((l) => (
                    <button key={l} type="button" onClick={() => switchLang(l)}
                      className={`px-2 py-0.5 rounded text-[11px] font-semibold uppercase border ${previewLang === l ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                      data-testid={`newsletter-lang-tab-${l}`}>
                      {l}
                    </button>
                  ))}
                </div>
              )}

              {previewSubject && (
                <p className="text-xs text-slate-500" data-testid="newsletter-preview-subject">
                  Oggetto: <span className="font-medium text-slate-700">{previewSubject}</span>
                </p>
              )}

              {previewAudience === 'guest' && previewLang !== 'it' && selected.status === 'draft' && (trDesc || trFailed) && (
                <div className="border rounded-md p-2 bg-slate-50" data-testid="newsletter-translation-box">
                  {trFailed && (
                    <p className="text-[11px] text-amber-700 font-medium mb-1 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="newsletter-translation-failed">
                      ⚠ Traduzione automatica in {previewLang.toUpperCase()} non riuscita: nell'anteprima viene mostrata la descrizione italiana. Riclicca la lingua per riprovare, oppure scrivi qui la traduzione e salvala.
                    </p>
                  )}
                  <label className="text-xs text-slate-500">
                    Descrizione tradotta automaticamente in {previewLang.toUpperCase()} — puoi correggerla
                  </label>
                  <Textarea rows={3} value={trDesc} onChange={(e) => setTrDesc(e.target.value)}
                    className="mt-1 text-xs" data-testid="newsletter-translation-input" />
                  <Button variant="outline" size="sm" className="mt-1 h-7 text-xs" onClick={saveTranslation}
                    data-testid="newsletter-translation-save-btn">
                    Salva traduzione {previewLang.toUpperCase()}
                  </Button>
                </div>
              )}

              <iframe title="preview" srcDoc={previewHtml} className="w-full border rounded-md bg-white"
                style={{ height: 560 }} data-testid="newsletter-preview-iframe" />

              {selected.status === 'scheduled' && (
                <div className="border-t pt-3 space-y-2" data-testid="newsletter-scheduled-panel">
                  <div className="flex items-center gap-2 text-sm bg-violet-50 border border-violet-200 rounded-md p-3 text-violet-900">
                    <CalendarClock className="h-4 w-4 shrink-0" />
                    <span>
                      Programmata per <strong>{fmtSlot(selected.scheduled_at)}</strong>
                      {' '}— gruppi: {(selected.scheduled_audiences || []).map((k) => AUDIENCES.find((a) => a.key === k)?.label || k).join(', ')}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={cancelSchedule} data-testid="newsletter-cancel-schedule-btn">
                      <XCircle className="h-4 w-4 mr-1" /> Annulla programmazione
                    </Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 ml-auto"
                      onClick={sendScheduledNow} disabled={busy === 'send'}
                      data-testid="newsletter-send-now-scheduled-btn">
                      {busy === 'send' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Invia subito adesso</>}
                    </Button>
                  </div>
                </div>
              )}

              {selected.status === 'draft' && (
                <div className="space-y-3 border-t pt-3">
                  <div className="flex flex-wrap gap-4">
                    {AUDIENCES.map((a) => (
                      <label key={a.key} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={audiencesOn[a.key]}
                          onCheckedChange={(v) => setAudiencesOn({ ...audiencesOn, [a.key]: !!v })}
                          data-testid={`newsletter-audience-check-${a.key}`} />
                        {a.label} <span className="text-[10px] text-slate-400">({a.hint})</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input className="w-64 h-8 text-sm" value={testEmail} placeholder="email per test"
                      onChange={(e) => setTestEmail(e.target.value)} data-testid="newsletter-test-email" />
                    <Button variant="outline" size="sm" onClick={sendTest} disabled={busy === 'test'}
                      data-testid="newsletter-test-btn">
                      {busy === 'test' ? <Loader2 className="h-4 w-4 animate-spin" /> : `Invia test (${previewAudience}${previewAudience === 'guest' ? ` · ${previewLang.toUpperCase()}` : ''})`}
                    </Button>
                    {selected.nl_type === 'lastminute' ? (
                      <div className="flex gap-2 ml-auto">
                        <Button variant="outline" size="sm" className="border-violet-400 text-violet-800 hover:bg-violet-50"
                          onClick={() => approveAndSend('schedule')} disabled={busy === 'send'}
                          data-testid="newsletter-schedule-btn">
                          <CalendarClock className="h-4 w-4 mr-1" /> Programma{nextSlot ? ` (${fmtSlot(nextSlot.slot_utc)})` : ''}
                        </Button>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => approveAndSend('now')} disabled={busy === 'send'}
                          data-testid="newsletter-send-btn">
                          {busy === 'send'
                            ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Invio…</>
                            : <><Zap className="h-4 w-4 mr-1" /> Invia subito (Last Minute)</>}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2 ml-auto">
                        <Button variant="outline" size="sm"
                          onClick={() => approveAndSend('now')} disabled={busy === 'send'}
                          data-testid="newsletter-send-btn">
                          <Send className="h-4 w-4 mr-1" /> Invia subito
                        </Button>
                        <Button size="sm" className="bg-violet-700 hover:bg-violet-800"
                          onClick={() => approveAndSend('schedule')} disabled={busy === 'send'}
                          data-testid="newsletter-schedule-btn">
                          {busy === 'send'
                            ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> …</>
                            : <><CalendarClock className="h-4 w-4 mr-1" /> Programma{nextSlot ? ` (${fmtSlot(nextSlot.slot_utc)})` : ' (gio 14:00)'}</>}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
